/**
 * stl-signup.js - Say The Label sign-up (slice 6).
 *
 * Three things share this file because they share one piece of state, "has this shopper signed
 * up": the welcome pop-up, the sign-up tab and the footer form. All three post to Klaviyo's
 * client subscribe API with the company id and list id printed on the form by Liquid, so no key
 * is typed here and the market amount and code are never computed in JavaScript.
 *
 * It also closes and opens the footer's four link groups when the viewport crosses 900px, because
 * the same markup is the phone accordion and the desktop columns.
 */
import { storage, toast, overlays, openOverlay, closeOverlay, onReady, config, KEYS } from '@stl/core';

const ON = 'stl-is-on';
const PHONE = '(max-width: 899px)';
const DIAL = { AU: '61', NZ: '64', US: '1', GB: '44', SG: '65', CN: '86' };
const $ = (sel, root) => (root || document).querySelector(sel);

/* ------------------------------------------------------------------ Klaviyo */

/** E.164 or nothing. A number we cannot be sure of is left out rather than sent and refused. */
function phoneE164(raw) {
  const value = String(raw || '').replace(/[^\d+]/g, '');
  if (!value) return '';
  if (value[0] === '+') return /^\+\d{8,15}$/.test(value) ? value : '';
  const dial = DIAL[(config().country || '').toUpperCase()];
  if (!dial) return '';
  const national = value.replace(/^0+/, '');
  const full = '+' + dial + national;
  return /^\+\d{8,15}$/.test(full) ? full : '';
}

/**
 * ISO date (YYYY-MM-DD) or nothing. The box asks for DD/MM/YYYY and shoppers type it every way
 * (24/9/1990, 24.09.90, 24-09-1990), and Klaviyo only treats a property as a DATE when it arrives
 * as ISO 8601. The first test sign-up (23 Sep 2026) reached Klaviyo as the text "11/06/1996", which
 * a date-based birthday flow can never trigger on, so this is what makes the birthday gift possible
 * at all. A two-digit year is read as 19xx or 20xx, whichever is not in the future. Anything that
 * is not a real calendar date in a plausible year returns '', and the caller asks again.
 */
/**
 * The birthday box types itself into DD-MM-YYYY: digits only, the two dashes appear on their own
 * after the day and the month, ten characters at most (Chris, 25 Sep 2026: "they can't just type
 * whatever text in there"). A dash is only added once the next digit is typed, so backspacing
 * over one does not put it straight back. birthdayISO below still accepts / . - and spaces, so a
 * pasted 15/06/1996 is fine too.
 */
function birthdayMask(input) {
  const digits = String(input.value || '').replace(/\D/g, '').slice(0, 8);
  let out = digits.slice(0, 2);
  if (digits.length > 2) out += '-' + digits.slice(2, 4);
  if (digits.length > 4) out += '-' + digits.slice(4, 8);
  if (input.value !== out) input.value = out;
}

function birthdayISO(raw) {
  const m = String(raw || '').trim().match(/^(\d{1,2})\s*[\/.\-\s]\s*(\d{1,2})\s*[\/.\-\s]\s*(\d{2}|\d{4})$/);
  if (!m) return '';
  const day = Number(m[1]);
  const month = Number(m[2]);
  let year = Number(m[3]);
  const now = new Date();
  if (m[3].length === 2) year += year + 2000 <= now.getFullYear() ? 2000 : 1900;
  if (year < 1900 || year > now.getFullYear()) return '';
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return '';
  if (date > now) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return year + '-' + pad(month) + '-' + pad(day);
}

/**
 * Posts one profile to the list. Resolves true on a 2xx. Never throws.
 * Shape: Klaviyo /client/subscriptions, revision 2025-01-15.
 *
 * THE REVISION IS LOAD-BEARING. `subscriptions` under the profile's attributes is only a valid
 * field from revision 2025-01-15. On 2024-10-15, which this file sent until 23 September 2026,
 * Klaviyo answered every single sign-up with
 *   400 "'subscriptions' is not a valid field for the resource 'profile'."
 *       at /data/attributes/profile/data/attributes/subscriptions
 * so the pop-up, the sign-up tab and the footer form all showed "Sorry, that did not go through"
 * and nobody could ever claim the welcome code. Measured against the live company id and list on
 * 23 Sep 2026 with a deliberately invalid email, so nothing was subscribed: the same payload that
 * fails on 2024-10-15 validates all the way to "Invalid email address" on 2025-01-15 and
 * 2025-04-15, which is the endpoint accepting the shape and stopping only at the address. Never
 * lower the revision without re-running that probe.
 */
async function subscribe(form, fields) {
  const company = form.getAttribute('data-stl-klaviyo-company');
  const list = form.getAttribute('data-stl-klaviyo-list');
  if (!company || !list) return false;

  const attributes = { email: fields.email };
  const subscriptions = { email: { marketing: { consent: 'SUBSCRIBED' } } };
  if (fields.first_name) attributes.first_name = fields.first_name;
  if (fields.phone) {
    attributes.phone_number = fields.phone;
    subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
  }
  if (fields.birthday) attributes.properties = { Birthday: fields.birthday };
  attributes.subscriptions = subscriptions;

  const body = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: form.getAttribute('data-stl-signup') || 'theme',
        profile: { data: { type: 'profile', attributes: attributes } },
      },
      relationships: { list: { data: { type: 'list', id: list } } },
    },
  };

  try {
    const response = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      headers: { 'content-type': 'application/json', revision: '2025-01-15' },
      body: JSON.stringify(body),
    });
    return response.ok;
  } catch (e) {
    return false;
  }
}

function say(form, text, isError) {
  const box = $('[data-stl-signup-msg]', form);
  if (!box) return;
  box.textContent = text || '';
  if (isError) box.setAttribute('data-stl-state', 'error');
  else box.removeAttribute('data-stl-state');
}

/* ------------------------------------------------------------------ state */

/* ?stl_pop=1 on any URL shows the pop-up again on a browser that has already signed up or closed it,
   so Chris and Sophie can check it on a phone they have used before (25 Sep 2026). It changes
   nothing in storage: the next plain page load is back to normal. */
const FORCE_POP = /[?&]stl_pop=1(&|$)/.test(window.location.search);

const state = {
  signed: !FORCE_POP && storage.get(KEYS.signed, false) === true,
  seen: !FORCE_POP && storage.get(KEYS.pop, false, 'session') === true,
  off: storage.get(KEYS.teaserOff, false) === true,
  ready: false,
  email: '',
};

/* ------------------------------------------------------------------ the tab */

function teaserEl() {
  return document.getElementById('stl-teaser');
}

function teaserSync() {
  const el = teaserEl();
  if (!el) return;
  el.classList.toggle(ON, state.ready && !state.signed && !state.off && !overlays.isBusy());
}

function teaserReady() {
  state.ready = true;
  teaserSync();
}

/* ------------------------------------------------------------------ the pop-up */

function popEl() {
  return document.getElementById('stl-pop');
}

function popStep(el, n) {
  el.querySelectorAll('[data-stl-pop-step]').forEach((step) => {
    step.hidden = step.getAttribute('data-stl-pop-step') !== String(n);
  });
}

function popMaybe() {
  const el = popEl();
  if (!el || state.seen || state.signed) return;
  if (el.classList.contains(ON) || overlays.anyOpen()) return;
  /* the Questions panel is docked, not on the overlay stack: never open the pop-up over Sophie mid-answer */
  if (document.documentElement.hasAttribute('data-stl-ask-docked')) return;
  const routes = (el.getAttribute('data-stl-pop-routes') || '').split(',').filter(Boolean);
  if (routes.indexOf(el.getAttribute('data-stl-template') || '') === -1) return;
  popStep(el, 1);
  openOverlay('stl-pop');
}

/** Everything that must happen once the pop-up is off the screen, however it closed. */
function popClosed() {
  state.seen = true;
  storage.set(KEYS.pop, true, 'session');
  const el = teaserEl();
  if (!el) return;
  const after = Number(el.getAttribute('data-stl-teaser-after-pop')) || 5;
  window.setTimeout(teaserReady, after * 1000);
}

/* ------------------------------------------------------------------ the footer accordion */

function footerSync(isPhone) {
  document.querySelectorAll('.stl-ftr-col').forEach((el) => {
    if (isPhone) el.removeAttribute('open');
    else el.setAttribute('open', '');
  });
}

/* ------------------------------------------------------------------ wiring */

onReady(() => {
  const pop = popEl();
  const teaser = teaserEl();

  /* the footer's link groups follow the viewport */
  if (document.querySelector('.stl-ftr-col') && window.matchMedia) {
    const mq = window.matchMedia(PHONE);
    const apply = (event) => footerSync(event.matches);
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);
  }

  /* forms */
  document.addEventListener('input', (event) => {
    const el = event.target;
    if (el && el.name === 'birthday' && el.closest('[data-stl-signup="popup-birthday"]')) birthdayMask(el);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest && event.target.closest('[data-stl-signup]');
    if (!form) return;
    event.preventDefault();
    if (!form.reportValidity()) return;

    const kind = form.getAttribute('data-stl-signup');
    const data = new FormData(form);
    const button = $('button[type="submit"]', form);
    if (button) button.setAttribute('aria-busy', 'true');
    say(form, '');

    let ok = false;
    if (kind === 'popup-birthday') {
      /* Step two is the birthday and nothing else, in every shape. All three shapes ask for the
         first name, the email and the mobile on step one - the flower lays them out in two rows
         rather than three (daisy-v4) - so this call is the original one again: the email that step
         one already sent, plus the birthday. v3's flower briefly asked for the name and the mobile
         here because its step one was email-only, and that branch is gone with it. */
      const typed = String(data.get('birthday') || '').trim();
      const birthday = birthdayISO(typed);
      if (typed && !birthday) {
        /* something was typed and it is not a date: ask once, keep the step open. A blank box is
           the shopper choosing not to say, and goes straight to the thank you. */
        if (button) button.removeAttribute('aria-busy');
        say(form, form.getAttribute('data-stl-birthday-error') || 'Please use DD/MM/YYYY', true);
        return;
      }
      ok = birthday ? await subscribe(form, { email: state.email, birthday: birthday }) : true;
      if (button) button.removeAttribute('aria-busy');
      /* the profile is already on the list; a failed birthday must not block the thank you */
      const el = popEl();
      if (el) closeOverlay('stl-pop');
      toast(form.getAttribute('data-stl-thanks') || '');
      return;
    }

    const email = String(data.get('email') || '').trim();
    const fields = {
      email: email,
      first_name: String(data.get('first_name') || '').trim(),
      phone: phoneE164(data.get('phone')),
    };
    ok = await subscribe(form, fields);
    if (button) button.removeAttribute('aria-busy');

    if (!ok) {
      say(form, form.getAttribute('data-stl-error') || 'Sorry, that did not go through. Please try again.', true);
      return;
    }

    state.email = email;
    state.signed = true;
    storage.set(KEYS.signed, true);
    teaserSync();

    if (kind === 'popup' && pop) {
      popStep(pop, 2);
      const next = $('[data-stl-pop-step="2"] input', pop);
      if (next) next.focus();
      return;
    }

    form.reset();
    const thanks = form.getAttribute('data-stl-thanks');
    if (thanks) toast(thanks);
  });

  /* the pop-up */
  if (pop) {
    /* a click on the dimmed background closes it, as in the mock */
    pop.addEventListener('click', (event) => {
      if (event.target === pop) closeOverlay('stl-pop');
    });
    const shop = $('[data-stl-pop-shop]', pop);
    if (shop) {
      shop.addEventListener('click', () => {
        state.seen = true;
        storage.set(KEYS.pop, true, 'session');
      });
    }
    const delay = Number(pop.getAttribute('data-stl-pop-delay')) || 12;
    const percent = Number(pop.getAttribute('data-stl-pop-scroll')) || 33;
    window.setTimeout(popMaybe, delay * 1000);
    window.addEventListener(
      'scroll',
      () => {
        if (state.seen || state.signed) return;
        if (window.scrollY > document.documentElement.scrollHeight * (percent / 100)) popMaybe();
      },
      { passive: true }
    );
  }

  /* the tab */
  if (teaser) {
    const open = $('[data-stl-teaser-open]', teaser);
    const close = $('[data-stl-teaser-close]', teaser);
    if (open) {
      open.addEventListener('click', () => {
        const el = popEl();
        if (!el) return;
        popStep(el, 1);
        /* the tab hides while the pop-up is open (teaserSync, on the overlay event), so the
           registry's own focus return finds nothing to land on; the overlay listener below puts
           focus back on the tab once teaserSync has shown it again */
        state.openedFromTab = true;
        openOverlay('stl-pop');
      });
    }
    if (close) {
      close.addEventListener('click', () => {
        state.off = true;
        storage.set(KEYS.teaserOff, true);
        teaserSync();
      });
    }
    const delay = Number(teaser.getAttribute('data-stl-teaser-delay')) || 15;
    window.setTimeout(() => {
      const el = popEl();
      if (!el || !el.classList.contains(ON)) teaserReady();
    }, delay * 1000);
  }

  document.addEventListener('stl:overlay:change', (event) => {
    const detail = event.detail || {};
    if (detail.id === 'stl-pop' && detail.open === false) popClosed();
    teaserSync();
    if (detail.id === 'stl-pop' && detail.open === false && state.openedFromTab) {
      state.openedFromTab = false;
      const tab = teaserEl();
      const opener = tab && $('[data-stl-teaser-open]', tab);
      if (opener && tab.classList.contains(ON)) {
        window.requestAnimationFrame(() => opener.focus({ preventScroll: true }));
      }
    }
  });

  teaserSync();
});
